import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import authApi from '../api/auth.api';
import { comptesApi } from '../api/clients.api';
import { useToast } from '../hooks/use-toast';
import { getRefreshToken } from '../api/client';

// Define User Profile Data Structure

interface UserData {
    accounts: any[];
    securityStatus: any;
    twoFactorEnabled: boolean | null;
    sessions: any[];
    isLoading: boolean;
    error: string | null;
    transactionCache?: Record<string, any[]>;
}

interface SessionContextType {
    lastActivity: Date;
    isIdle: boolean;
    userData: UserData;
    selectedAccount: any | null;
    setSelectedAccount: (account: any) => void;
    refreshUserData: () => Promise<void>;
    updateTransactionCache: (key: string, data: any[]) => void;
}

const SessionContext = createContext<SessionContextType>({
    lastActivity: new Date(),
    isIdle: false,
    userData: {
        accounts: [],
        securityStatus: null,
        twoFactorEnabled: null,
        sessions: [],
        isLoading: true,
        error: null,
        transactionCache: {}
    },
    selectedAccount: null,
    setSelectedAccount: () => { },
    refreshUserData: async () => { },
    updateTransactionCache: () => { },
});

export const useSession = () => useContext(SessionContext);



interface SessionProviderProps {
    children: React.ReactNode;
    timeoutMinutes?: number;
    warningMinutes?: number;
    heartbeatMinutes?: number;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({
    children,
    timeoutMinutes = 15,
    warningMinutes = 13,
    heartbeatMinutes = 5,
}) => {
    const { user, logout } = useAuth();
    const { toast } = useToast();
    const navigate = useNavigate();

    // Session State
    const [lastActivity, setLastActivity] = useState<Date>(new Date());
    const [isIdle, setIsIdle] = useState(false);

    // Profile Data Cache
    const [userData, setUserData] = useState<UserData>({
        accounts: [],
        securityStatus: null,
        twoFactorEnabled: null,
        sessions: [],
        isLoading: true,
        error: null,
        transactionCache: {} // Initialize cache
    });

    // Global selected account state (shared between Navbar and Home)
    const [selectedAccount, setSelectedAccount] = useState<any | null>(null);

    // Wrapper to update cache from components
    const updateTransactionCache = (key: string, data: any[]) => {
        setUserData(prev => ({
            ...prev,
            transactionCache: {
                ...prev.transactionCache,
                [key]: data
            }
        }));
    };

    // Refs
    const lastActivityRef = useRef<Date>(new Date());
    const hasActivitySinceLastHeartbeat = useRef<boolean>(false);
    const activityThrottler = useRef<NodeJS.Timeout | null>(null);
    const dataFetchedRef = useRef(false);

    // ==========================================
    // DATA FETCHING LOGIC
    // ==========================================
    const fetchUserData = useCallback(async (force = false) => {
        if (!user) return;
        if (dataFetchedRef.current && !force) return;

        setUserData(prev => ({ ...prev, isLoading: force ? true : prev.isLoading }));

        try {
            console.log("🔄 Fetching User Data (Accounts, Security, 2FA, Sessions)...");
            const [accountsRes, securityRes, twoFactorRes, sessionsRes] = await Promise.all([
                comptesApi.getMyAccounts().catch(e => { console.error(e); return { results: [] }; }),
                authApi.getSecurityStatus().catch(e => { console.error(e); return null; }),
                authApi.get2FAStatus().catch(e => { console.error(e); return { twoFactorEnabled: false }; }),
                authApi.listSessions().catch(e => { console.error(e); return []; })
            ]);

            setUserData(prev => ({ // Preserve existing transaction cache
                ...prev,
                accounts: accountsRes.results || [],
                securityStatus: securityRes,
                twoFactorEnabled: twoFactorRes.twoFactorEnabled,
                sessions: sessionsRes,
                isLoading: false,
                error: null
            }));
            dataFetchedRef.current = true;
        } catch (error) {
            console.error("Error fetching user data:", error);
            setUserData(prev => ({ ...prev, isLoading: false, error: "Failed to load profile data" }));
        }
    }, [user]);

    // Fetch on mount (or when user changes)
    useEffect(() => {
        if (user) {
            fetchUserData();
        } else {
            // Reset if logged out
            setUserData({
                accounts: [],
                securityStatus: null,
                twoFactorEnabled: null,
                sessions: [],
                isLoading: true,
                error: null
            });
            dataFetchedRef.current = false;
        }
    }, [user, fetchUserData]);

    // Public refresh function
    const refreshUserData = async () => {
        await fetchUserData(true);
    };

    // ==========================================
    // SESSION LOGIC (Unchanged)
    // ==========================================
    const handleActivity = () => {
        const now = new Date();
        lastActivityRef.current = now;
        hasActivitySinceLastHeartbeat.current = true;
        setIsIdle(false);

        if (!activityThrottler.current) {
            setLastActivity(now);
            activityThrottler.current = setTimeout(() => {
                activityThrottler.current = null;
            }, 30000);
        }
    };

    useEffect(() => {
        if (!user) return;
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
        events.forEach(event => window.addEventListener(event, handleActivity));
        return () => {
            events.forEach(event => window.removeEventListener(event, handleActivity));
            if (activityThrottler.current) clearTimeout(activityThrottler.current);
        };
    }, [user]);

    useEffect(() => {
        if (!user) return;
        const heartbeatInterval = setInterval(async () => {
            const refreshToken = getRefreshToken();
            if (hasActivitySinceLastHeartbeat.current && refreshToken) {
                try {
                    await authApi.refreshToken({ refreshToken });
                    hasActivitySinceLastHeartbeat.current = false;
                } catch (error) {
                    console.error('Heartbeat failed', error);
                }
            }
        }, heartbeatMinutes * 60 * 1000);
        return () => clearInterval(heartbeatInterval);
    }, [user, heartbeatMinutes]);

    useEffect(() => {
        if (!user) return;
        const checkInterval = setInterval(() => {
            const now = new Date();
            const timeSinceLastActivity = now.getTime() - lastActivityRef.current.getTime();
            const minutesSinceActivity = timeSinceLastActivity / (1000 * 60);

            if (minutesSinceActivity >= warningMinutes && minutesSinceActivity < timeoutMinutes) {
                if (!isIdle) {
                    setIsIdle(true);
                    toast({ title: "Atansyon: Inaktivite", description: `Expire nan ${timeoutMinutes - warningMinutes} min...`, type: "warning" });
                }
            }

            if (minutesSinceActivity >= timeoutMinutes) {
                logout();
                navigate('/login');
                toast({ title: "Session Ekspire", description: "Dekonekte akòz inaktivite.", type: "error" });
            }
        }, 30000);
        return () => clearInterval(checkInterval);
    }, [user, timeoutMinutes, warningMinutes, navigate, logout, isIdle, toast]);

    return (
        <SessionContext.Provider value={{ lastActivity, isIdle, userData, selectedAccount, setSelectedAccount, refreshUserData, updateTransactionCache }}>
            {children}
        </SessionContext.Provider>
    );
};
